import { motion } from "framer-motion";
import { Users, Check, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { HuntSourceStatus, sourceIssues } from "./HuntSourceStatus";

const modeLabel = (mode) => (mode === "hunt" ? "Hunt" : mode === "farm" ? "Farm" : mode);

// Inline panel shown after a run: summary + "View N Results" that opens the modal.
export const ResultsSummaryPanel = ({ results, onViewResults, onBack, savedCount = 0, dismissedCount = 0 }) => {
  const found = results.companiesFound?.length || 0;
  const remaining = Math.max(0, found - savedCount - dismissedCount);
  return (
    <div className="flex-1 flex flex-col overflow-hidden relative z-10">
      <div className="mb-4 flex items-center justify-between flex-shrink-0">
        <h3 className="text-lg text-ism-teal/80">{modeLabel(results.mode)} Results</h3>
        <div className="flex items-center gap-2">
          {found > 0 && (
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button onClick={onViewResults} size="sm" className="bg-ism-teal hover:bg-ism-teal/90 text-black font-semibold rounded-lg" data-testid="view-results-btn">
                <Eye className="h-4 w-4 mr-1" />
                {remaining > 0 ? `Review ${remaining} Remaining` : `View ${found} Results`}
              </Button>
            </motion.div>
          )}
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button onClick={onBack} size="sm" className="bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white border border-white/10 rounded-lg">
              Back
            </Button>
          </motion.div>
        </div>
      </div>

      <ScrollArea className="flex-1 pr-4">
        {results.needsAccounts ? (
          <motion.div className="text-center py-12" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="p-6 rounded-2xl bg-gradient-to-br from-ism-teal/10 to-ism-teal/5 border border-white/10 inline-block mb-4">
              <Users className="h-12 w-12 text-ism-teal" />
            </div>
            <p className="text-gray-400 mb-4">{results.message}</p>
            <Button onClick={() => toast.info("Navigate to Accounts to add your accounts")} size="sm"
              className="bg-gradient-to-r from-ism-teal to-ism-teal/80 hover:from-ism-teal/90 hover:to-ism-teal/70 text-black font-semibold rounded-lg">
              Go to Accounts
            </Button>
          </motion.div>
        ) : found > 0 ? (
          <motion.div className="text-center py-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="p-4 rounded-2xl bg-gradient-to-br from-ism-teal/10 to-ism-teal/5 border border-ism-teal/20 inline-block mb-4">
              <Check className="h-10 w-10 text-ism-teal" />
            </div>
            <p className="text-white font-medium mb-2" data-testid="results-summary-headline">
              {remaining === 0 ? `All ${found} prospects dispositioned` : savedCount + dismissedCount > 0 ? `${remaining} of ${found} prospects still need a decision` : `${found} prospects found!`}
            </p>
            <p className="text-gray-400 text-sm mb-4">
              {remaining === 0 ? `${savedCount} saved to CRM · ${dismissedCount} dismissed — you can hunt again` : "Click \"Review\" to save or dismiss each one"}
            </p>
            <div className="inline-block"><HuntSourceStatus status={results.data?.searchStats?.sourceStatus} idPrefix="hunt-summary-source" /></div>
          </motion.div>
        ) : (
          <NoResults status={results.data?.searchStats?.sourceStatus} />
        )}
      </ScrollArea>
    </div>
  );
};

const NoResults = ({ status }) => {
  const down = sourceIssues(status).filter((i) => i.tone === "down");
  return (
    <div className="text-center py-8 px-2" data-testid="hunt-no-results">
      <p className="text-gray-400 text-sm">{down.length ? "No prospects — a data source this hunt depends on was unavailable" : "No results found"}</p>
      {down.length > 0 && (
        <ul className="mt-3 space-y-1 text-left inline-block" data-testid="hunt-no-results-sources">
          {down.map((i) => <li key={i.key} className="text-xs text-red-300">✕ {i.label} <span className="text-gray-500">— {i.detail}</span></li>)}
        </ul>
      )}
      <HuntSourceStatus status={status} compact idPrefix="hunt-summary-source" />
    </div>
  );
};
